import { trackEvent } from "@intercom/messenger-js-sdk";
import { useEffect, useMemo, useState } from "react";
import {
  ActionIcon,
  Box,
  Button,
  Flex,
  Group,
  Loader,
  Menu,
  ScrollArea,
  Stack,
  Title,
  Tooltip,
} from "@mantine/core";
import { useClipboard } from "@mantine/hooks";
import {
  IconDotsVertical,
  IconSparkles,
  IconTrash,
  IconUpload,
} from "@tabler/icons-react";
import {
  GraphQLQuery,
  GraphQLSubscription,
  generateClient,
  put,
} from "@aws-amplify/api";
import { getUrl } from "@aws-amplify/storage";
import { FetchUserAttributesOutput } from "aws-amplify/auth";
import { fetchUserAttributes } from "aws-amplify/auth";
import { mdiContentCopy } from "@mdi/js";
import Icon from "@mdi/react";
import SearchInput from "@/components/Input/SearchInput";
import CustomAudioPlayer from "@/components/Record/CustomAudioPlayer";
import {
  appShowNotification,
  deleteQuickRecordItemFromIndexedDB,
  getQuickRecordItemsFromIndexedDB,
} from "@/utils/utils";
import { deleteRecording } from "@/graphql/mutations";
import {
  onCreateRecording,
  onDeleteRecording,
  onUpdateRecording,
} from "@/graphql/subscriptions";
import {
  DeleteRecordingMutation,
  DeleteRecordingMutationVariables,
  OnCreateRecordingSubscription,
  OnDeleteRecordingSubscription,
  OnUpdateRecordingSubscription,
  RecordingsByLIDQueryVariables,
} from "@/API";
import QuickRecordAddCaseModal from "./AddCaseModal";
import QuickRecordConfirmDeleteModal from "./ConfirmDeleteModal";

const client = generateClient();

// Simple GraphQL query for recordings
const quickRecordingsByLID = /* GraphQL */ `query QuickRecordingsByLID(
  $lID: ID!
  $sortDirection: ModelSortDirection
  $filter: ModelRecordingFilterInput
  $limit: Int
  $nextToken: String
) {
  recordingsByLID(
    lID: $lID
    sortDirection: $sortDirection
    filter: $filter
    limit: $limit
    nextToken: $nextToken
  ) {
    items {
      id
      name
      storagePath
      duration
      text
      summary
      lID
      cID
      createdAt
      updatedAt
      _version
      _deleted
      __typename
    }
    nextToken
    __typename
  }
}`;

const formatDate = (date: string) => {
  if (!date) return "";
  return new Date(date).toLocaleString([], {
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

interface RecordingsProps {
  onViewCases?: () => void;
}

interface RecordingItem {
  id: string;
  name: string;
  storagePath: string;
  duration: number;
  text?: string;
  summary?: string;
  lID: string;
  cID?: string;
  createdAt: string;
  updatedAt: string;
  _version: number;
  _deleted?: boolean;
  __typename: string;
  file?: string | null;
  date?: string;
  key?: string;
}

const Recordings = ({ onViewCases }: RecordingsProps) => {
  const clipboard = useClipboard();
  const [recordings, setRecordings] = useState<RecordingItem[]>([]);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<"all" | "unassigned" | "assigned">("all");
  const [loading, setLoading] = useState(false);
  const [deleteRecord, setDeleteRecord] = useState<RecordingItem | null>(null);
  const [addCaseRecord, setAddCaseRecord] = useState<RecordingItem | null>(null);


  useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Set up subscriptions for real-time updates
    const subscriptions = [
      createSubAddRecording(),
      createSubUpdateRecording(),
      createSubDeleteRecording(),
    ];

    return () => {
      subscriptions.forEach((unsub) => unsub());
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredRecordings = useMemo(() => {
    let filtered = [...recordings];
    
    if (filter === "unassigned") {
      filtered = filtered.filter((item) => !item.cID);
    } else if (filter === "assigned") {
      filtered = filtered.filter((item) => item.cID);
    }
    
    if (search.trim() !== "") {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter((item) => {
        const nameMatch = item?.name?.toLowerCase?.()?.includes?.(searchLower);
        const dateStr = formatDate(item.date || item.createdAt);
        const dateMatch = dateStr.toLowerCase().includes(searchLower);
        return nameMatch || dateMatch;
      });
    }
    
    return filtered.sort((a, b) => {
      const aDate = new Date(a.date || a.createdAt).getTime();
      const bDate = new Date(b.date || b.createdAt).getTime();
      return bDate - aDate; // Most recent first
    });
  }, [recordings, filter, search]);

  const createSubAddRecording = () => {
    const createSub = client
      .graphql<GraphQLSubscription<OnCreateRecordingSubscription>>({
        query: onCreateRecording,
      })
      .subscribe({
        next: () => {
          trackEvent("create_recording");
          console.log('[SUBSCRIPTION] New recording created, refreshing list');
          fetchData();
        },
        error: (error) => console.warn(error),
      });
    return () => createSub.unsubscribe();
  };

  const createSubUpdateRecording = () => {
    const updateSub = client
      .graphql<GraphQLSubscription<OnUpdateRecordingSubscription>>({
        query: onUpdateRecording,
      })
      .subscribe({
        next: async ({ data }) => {
          const updatedRecording = data.onUpdateRecording;
          console.log('[SUBSCRIPTION] Recording updated:', updatedRecording?.id, 'has transcription:', !!updatedRecording?.text);
          
          // Only migrate when transcription first arrives (prevent loops)
          if (updatedRecording && updatedRecording.text && updatedRecording.text.length > 0) {
            console.log('[SUBSCRIPTION] Transcription detected, attempting single migration...');
            
            // Use setTimeout to prevent blocking and reduce loop risk
            setTimeout(async () => {
              try {
                const { DatabaseMigrationAdapter } = await import("@/services/migration");
                const migrationAdapter = DatabaseMigrationAdapter.getInstance();
                
                // Direct migration - let Edge Function handle duplicates
                await migrationAdapter.handleRecordingOperation(
                  'CREATE', 
                  updatedRecording as Record<string, unknown>, 
                  { data: { createRecording: updatedRecording } } as Record<string, unknown>
                );
                console.log('[SUBSCRIPTION] Migration successful for:', updatedRecording.id);
              } catch (syncError) {
                console.warn('[SUBSCRIPTION] Migration failed for:', updatedRecording.id, syncError);
              }
            }, 100); // Small delay to prevent immediate loops
          }
          
          // Always refresh UI to show updates
          console.log('[SUBSCRIPTION] Refreshing UI after recording update');
          fetchData();
        },
        error: (error) => console.warn('[SUBSCRIPTION] Update subscription error:', error),
      });
    return () => updateSub.unsubscribe();
  };

  const createSubDeleteRecording = () => {
    const deleteSub = client
      .graphql<GraphQLSubscription<OnDeleteRecordingSubscription>>({
        query: onDeleteRecording,
      })
      .subscribe({
        next: () => {
          console.log('[SUBSCRIPTION] Recording deleted, refreshing list');
          fetchData();
        },
        error: (error) => console.warn(error),
      });
    return () => deleteSub.unsubscribe();
  };

  // No separate migration function needed - handled directly in subscription

  const fetchData = async () => {
    setLoading(true);
    const items = (await getQuickRecordItemsFromIndexedDB()) as Array<{
      key: string;
      recordingItem: RecordingItem;
    }>;

    try {
      const userInfo: FetchUserAttributesOutput = await fetchUserAttributes();
      if (userInfo["custom:location"] === undefined) {
        setLoading(false);
        return;
      }

      const recordingQueryInput: RecordingsByLIDQueryVariables = {
        lID: userInfo["custom:location"],
      };

      let listData: RecordingItem[] = [];

      try {
        const recordingData = await client.graphql({
          query: quickRecordingsByLID,
          variables: recordingQueryInput,
        });

        listData =
          "data" in recordingData
            ? recordingData?.data?.recordingsByLID?.items || []
            : [];
      } catch (graphqlError) {
        console.warn("Failed to fetch recordings from Amplify:", graphqlError);
        listData = [];
      }

      // Process recordings to get file URLs
      const updatedListData = await Promise.all(
        listData.map(async (item: RecordingItem) => {
          if (!item) return null;

          try {
            const filePath = item.storagePath;
            let file = null;

            // Try to get file URL for any recording with a storage path
            if (filePath) {
              try {
                // Handle both "private/" prefixed paths and clean paths
                const path = filePath.includes("private") ? filePath : `private/${filePath}`;
                const fileUrl = await getUrl({
                  path: path,
                  options: {
                    validateObjectExistence: true,
                  },
                });
                file = fileUrl.url.toString();
                console.log(`[RECORDINGS] Generated file URL for ${item.name}:`, !!file);
              } catch (urlError) {
                console.warn(`[RECORDINGS] Failed to generate URL for ${item.name}:`, urlError);
                // If the path fails, try alternative path formats
                try {
                  const alternativePath = filePath.includes("private") 
                    ? filePath.replace("private/", "") 
                    : `private/${filePath}`;
                  const fileUrl = await getUrl({
                    path: alternativePath,
                    options: {
                      validateObjectExistence: true,
                    },
                  });
                  file = fileUrl.url.toString();
                  console.log(`[RECORDINGS] Generated file URL with alternative path for ${item.name}:`, !!file);
                } catch (altError) {
                  console.warn(`[RECORDINGS] Both path formats failed for ${item.name}`);
                }
              }
            }

            return {
              ...item,
              file: file,
              date: item.createdAt,
            };
          } catch (error) {
            console.warn(`[RECORDINGS] Error processing recording ${item.name}:`, error);
            return {
              ...item,
              date: item.createdAt,
            };
          }
        })
      );

      // Filter out null results
      const filteredListData = updatedListData.filter(
        (item) => item !== null
      );

      // No migration in fetchData - only subscriptions handle migration to prevent loops

      // Combine Amplify recordings with IndexedDB items
      const allRecordings = [
        ...filteredListData,
        ...items.map((item) => ({
          ...item.recordingItem,
          key: item.key,
        })),
      ];

      setRecordings(allRecordings);
    } catch (error) {
      if (items.length > 0) {
        setRecordings(
          items.map((item) => ({
            ...item.recordingItem,
            key: item.key,
          }))
        );
      } else {
        setRecordings([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRecord = async () => {
    if (!deleteRecord) return;

    try {
      if (deleteRecord.id && deleteRecord._version) {
        const varsDeleteRecording: DeleteRecordingMutationVariables = {
          input: {
            id: deleteRecord.id,
            _version: deleteRecord._version,
          },
        };

        const deleteResult = await client.graphql<
          GraphQLQuery<DeleteRecordingMutation>
        >({
          query: deleteRecording,
          variables: varsDeleteRecording,
        });

        if (deleteResult.data?.deleteRecording) {
          setRecordings(
            recordings.filter((item) => item.id !== deleteRecord.id)
          );
          appShowNotification({
            type: "success",
            title: "Recording deleted",
            message: "Recording has been permanently deleted",
          });
        } else {
          throw new Error("Amplify deletion failed");
        }
      } else if (deleteRecord.key) {
        await deleteQuickRecordItemFromIndexedDB(deleteRecord.key);

        setRecordings(
          recordings.filter((item) => item.key !== deleteRecord.key)
        );

        appShowNotification({
          type: "success",
          title: "Recording deleted",
          message: "Local recording has been permanently deleted",
        });
      } else {
        throw new Error("Unable to identify record type");
      }
    } catch (error) {
      appShowNotification({
        type: "error",
        title: "Delete failed",
        message:
          error instanceof Error
            ? error.message
            : "Failed to delete recording. Please try again.",
      });
    }
    setDeleteRecord(null);
  };

  const handleSummarizeRecording = async (recordingId: string) => {
    try {
      const init = {
        response: false,
        headers: {},
        body: {},
      };

      const summarizeResult = put({
        apiName: "SummarizeRecording",
        path: `/summarizerecording/${recordingId}`,
        options: init,
      });
      
      await summarizeResult.response;
    } catch (error) {
      console.warn("Summarize error:", error);
    }
  };

  const renderUnassignedRecordingMenu = (recording: RecordingItem) => (
    <Flex align="center">
      <Tooltip withArrow label="Upload record">
        <ActionIcon
          variant="subtle"
          onClick={() => setAddCaseRecord(recording)}
        >
          <IconUpload size="20" />
        </ActionIcon>
      </Tooltip>
      <Menu position="bottom-end" shadow="md" withArrow radius="sm">
        <Menu.Target>
          <Tooltip withArrow label="Menu">
            <ActionIcon variant="subtle">
              <IconDotsVertical size="20" color="black" />
            </ActionIcon>
          </Tooltip>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Item
            c="red"
            leftSection={<IconTrash size={20} />}
            onClick={() => setDeleteRecord(recording)}
          >
            Delete
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
    </Flex>
  );

  const renderAssignedRecordingMenu = (recording: RecordingItem) => (
    <Flex align="center">
      <Menu position="bottom-end" shadow="md" withArrow radius="sm">
        <Menu.Target>
          <Tooltip withArrow label="Menu">
            <ActionIcon variant="subtle">
              <IconDotsVertical size="20" color="black" />
            </ActionIcon>
          </Tooltip>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Item
            leftSection={<Icon path={mdiContentCopy} size={0.8} />}
            onClick={() => {
              clipboard.copy(recording.text || "");
              appShowNotification({
                title: "Copied text to clipboard",
                type: "success",
              });
            }}
          >
            Copy text
          </Menu.Item>
          {recording.summary && (
            <Menu.Item
              leftSection={<Icon path={mdiContentCopy} size={0.8} />}
              onClick={() => {
                clipboard.copy(recording.summary || "");
                appShowNotification({
                  title: "Copied summary to clipboard",
                  type: "success",
                });
              }}
            >
              Copy summary
            </Menu.Item>
          )}
          <Menu.Item
            leftSection={<IconSparkles size={20} />}
            onClick={() => handleSummarizeRecording(recording.id)}
          >
            Summarize
          </Menu.Item>
          <Menu.Divider />
          <Menu.Item
            c="red"
            leftSection={<IconTrash size={20} />}
            onClick={() => setDeleteRecord(recording)}
          >
            Delete
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
    </Flex>
  );

  return (
    <Stack gap={30} h="100%" p={20}>
      <Group justify="space-between">
        <Title size="20" c="#2D2D2D">
          Recordings
        </Title>
      </Group>
      
      <SearchInput
        search={search}
        placeholder="Search recordings..."
        onClearSearch={() => setSearch("")}
        onSearchInputChange={(event: React.ChangeEvent<HTMLInputElement>) =>
          setSearch(event.target.value)
        }
      />
      
      <Group gap={10}>
        <Button
          variant={filter === "all" ? "primary" : "outline"}
          radius="md"
          onClick={() => setFilter("all")}
        >
          All
        </Button>
        <Button
          variant={filter === "unassigned" ? "primary" : "outline"}
          radius="md"
          onClick={() => setFilter("unassigned")}
        >
          Unassigned
        </Button>
        <Button
          variant={filter === "assigned" ? "primary" : "outline"}
          radius="md"
          onClick={() => setFilter("assigned")}
        >
          Assigned
        </Button>
      </Group>
      
      <ScrollArea.Autosize h="calc(100vh - 320px)" pr={5}>
        {loading && (
          <Flex h={50} mb={30} w="100%" align="center" justify="center">
            <Loader className="loading-icon" size="md" type="dots" />
          </Flex>
        )}
        
        {filteredRecordings?.map((recording) => (
          <Box p={0} mb={10} key={recording.id || recording.key}>
            <CustomAudioPlayer
              isQuickRecord={true}
              recordItem={recording}
              renderActions={() =>
                recording.cID
                  ? renderAssignedRecordingMenu(recording)
                  : renderUnassignedRecordingMenu(recording)
              }
            />
          </Box>
        ))}
      </ScrollArea.Autosize>
      
      <QuickRecordConfirmDeleteModal
        modalOpened={deleteRecord !== null}
        setModalOpened={(value: boolean | null) =>
          setDeleteRecord(value ? deleteRecord : null)
        }
        onConfirm={handleDeleteRecord}
        deleting={false}
        title="Do you want to delete this recording?"
        description="This will delete all associated data and CANNOT be undone"
      />
      
      <QuickRecordAddCaseModal
        modalOpened={addCaseRecord !== null}
        setModalOpened={(value: boolean | null) =>
          setAddCaseRecord(value ? addCaseRecord : null)
        }
        recordingItem={addCaseRecord}
        onViewCases={onViewCases}
        refetchRecordings={fetchData}
      />
    </Stack>
  );
};

export default Recordings;